import { SearchResult, SectionActiveStates } from './SearchContext';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import groupBy from 'lodash/groupBy';
import { ConfigurationType } from '../types';
import { translateNewConfigurationTypeKeysToOldVersion } from '../helpers';
import { humanizeDate } from '../../../helpers/humanizeDate';
import { isItemActive } from '../../helpers';
import { FunnelFieldLabels } from '../../../../constants/deprecated_fieldsManagementConsts';
import { SweepFieldTypes } from '../../../../types/enums/SweepFieldTypes';
import { searchInDOM, isSectionReadyForDOMSearch } from './domSearchHelpers';
import React from 'react';
import { ActiveState } from '../../activity-state/helpers';

/**
 * Filters out the first h4 heading (and its content) and the second h4 heading
 * from the markdown text, keeping only the content starting from the paragraph
 * after the second h4.
 */
export const filterOpenAiContent = (text: string): string => {
  // Split the text into lines
  const lines = text.split('\n');
  const result: string[] = [];
  let h4Count = 0;
  let skipUntilNextSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isH4 = line.trim().startsWith('####');

    if (isH4) {
      h4Count++;

      if (h4Count === 1) {
        // Skip the first h4 and all content until the next h4
        skipUntilNextSection = true;
        continue;
      } else if (h4Count === 2) {
        // Skip the second h4 but start including content after it
        skipUntilNextSection = false;
        continue;
      } else {
        // Include third h4 and onwards
        skipUntilNextSection = false;
        result.push(line);
      }
    } else {
      // For non-h4 lines
      if (skipUntilNextSection) {
        // Skip content after first h4 until we hit second h4
        continue;
      } else {
        // Include all other content
        result.push(line);
      }
    }
  }

  return result.join('\n').trim();
};

interface SearchMatch {
  text: string;
  start: number;
  end: number;
}

/**
 * Mapping of configuration types to their searchable fields
 * Based on the columns actually rendered in each table component
 */
const SEARCHABLE_FIELDS_BY_TYPE: Record<string, string[]> = {
  [ConfigurationType.fields]: ['label', 'objectName', 'type'],
  [ConfigurationType.rollups]: ['label', 'objectName', 'type'],
  [ConfigurationType.flows]: ['label', 'type', 'lastModified'],
  [ConfigurationType.processBuilderFlows]: ['label', 'type'],
  [ConfigurationType.objects]: ['label', 'name'],
  [ConfigurationType.relatedObjects]: ['label', 'objectName'],
  default: ['label', 'lastModified'],
  relatedList: ['label', 'objectName'],
};

/**
 * Get searchable fields for a given configuration type
 * Falls back to ['default'] if standard type not found
 */
const getSearchableFields = (configurationType: string): string[] => {
  return SEARCHABLE_FIELDS_BY_TYPE[configurationType] || SEARCHABLE_FIELDS_BY_TYPE['default'];
};

const findMatchesInText = (text: string, searchTerm: string): SearchMatch[] => {
  if (!text || !searchTerm) return [];

  const matches: SearchMatch[] = [];
  const regex = new RegExp(searchTerm, 'gi');
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push({
      text: match[0],
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  return matches;
};

/**
 * Search for a term within a text and generate SearchResult objects
 */
const searchInText = (
  text: string,
  searchTerm: string,
  field: string,
  section: SearchResult['section'],
  tableType?: string,
  rowIndex?: number,
  rowId?: string,
): SearchResult[] => {
  if (!text || !searchTerm) return [];

  const matches = findMatchesInText(text, searchTerm);
  return matches.map((match, matchIdx) => ({
    text: match.text,
    sourceText: text, // Store the full source text for exact matching
    section,
    tableType,
    rowIndex,
    rowId,
    field,
    matchIndex: 0,
    occurrenceIndex: matchIdx, // Local occurrence index within this element
  }));
};

/**
 * Helper function to flatten nested 'others' items into separate types
 * This mirrors the logic in DependenciesTable where 'others' items are grouped by type
 */
const flattenConfigurationItems = (
  configurationItems: ConfigurationItemMap,
): Record<string, any[]> => {
  const flatten: Record<string, any[]> = {};

  Object.entries(configurationItems).forEach(([key, items]) => {
    if (key === ConfigurationType.others) {
      // Group 'others' items by their type, just like in DependenciesTable
      const itemsWithTranslatedKeys = items.map((item: any) => ({
        ...item,
        type: translateNewConfigurationTypeKeysToOldVersion(item.type as string),
      }));
      const groupedItems = groupBy(itemsWithTranslatedKeys, 'type');

      // Add each grouped type as a separate entry
      Object.entries(groupedItems).forEach(([groupedType, groupedItems]) => {
        flatten[groupedType] = groupedItems;
      });
    } else {
      flatten[key] = items;
    }
  });

  return flatten;
};

/**
 * Helper function to translate objectName to its human-readable label
 */
const translateObjectName = (
  objectName: string,
  saltoObjectTypes?: Pick<{ objectType: string; label: string }, 'objectType' | 'label'>[],
): string => {
  if (!saltoObjectTypes) return objectName;
  return saltoObjectTypes.find((type) => type.objectType === objectName)?.label ?? objectName;
};

/**
 * Helper function to translate lastModified timestamp to human-readable date
 * Uses the same format as displayed in tables (SHORT_WITHOUT_TIME variant)
 */
const translateLastModified = (lastModified: string | number): string => {
  if (!lastModified) return '';
  return humanizeDate({ dateOrTimestamp: lastModified });
};

/**
 * Helper function to get the date value from an item
 * Raw data has updatedAt or annotations.changedAt, not lastModified
 */
const getDateValueFromItem = (item: any): string => {
  return item.updatedAt ?? item?.annotations?.changedAt ?? '';
};

/**
 * Helper function to search through table items
 * Extracts common logic for searching through configuration items
 */
const searchThroughTableItems = (
  items: ConfigurationItemMap,
  section: SearchResult['section'],
  lowerSearchTerm: string,
  globalMatchIndex: { value: number },
  saltoObjectTypes?: Pick<{ objectType: string; label: string }, 'objectType' | 'label'>[],
  onlyActive?: boolean,
): SearchResult[] => {
  const results: SearchResult[] = [];
  const flattenItems = flattenConfigurationItems(items);

  Object.entries(flattenItems).forEach(([tableType, items]) => {
    const searchableFields = getSearchableFields(tableType);
    // Filter items based on active state to match table rendering
    // If onlyActive is undefined, default to filtering out inactive items for backwards compatibility
    const filteredItems = onlyActive === false ? items : items.filter(isItemActive);

    filteredItems.forEach((item: any, rowIndex: number) => {
      searchableFields.forEach((field) => {
        let value = item[field];

        // Translate objectName to its label for searching (except for relatedList which has all data)
        if (field === 'objectName' && value && tableType !== 'relatedList') {
          value = translateObjectName(value, saltoObjectTypes);
        }

        // Translate lastModified timestamp to human-readable date
        // Raw data has updatedAt/annotations.changedAt, not lastModified
        if (field === 'lastModified') {
          const dateValue = getDateValueFromItem(item);
          if (dateValue) {
            value = translateLastModified(dateValue);
          }
        }

        // Translate field type to its label for searching (e.g., "LongTextArea" -> "Long Text Area")
        if (
          field === 'type' &&
          value &&
          (tableType === ConfigurationType.fields || tableType === ConfigurationType.rollups)
        ) {
          const translatedType = FunnelFieldLabels[value as SweepFieldTypes];
          if (translatedType) {
            value = translatedType;
          }
        }

        // Don't search against configuration type names (table headers)
        if (value && typeof value === 'string' && value !== tableType) {
          const matches = searchInText(
            value,
            lowerSearchTerm,
            field,
            section,
            tableType,
            rowIndex,
            item.id, // Pass the item ID for precise row identification
          );

          matches.forEach((match) => {
            results.push({
              ...match,
              matchIndex: globalMatchIndex.value++,
            });
          });
        }
      });
    });
  });

  return results;
};

export const searchThroughAllData = (
  searchTerm: string,
  allData: {
    dependsOnItems?: ConfigurationItemMap;
    usedByItems?: ConfigurationItemMap;
    usedBySweepItems?: ConfigurationItemMap;
    configurationItems?: ConfigurationItemMap;
    sweepConfigurationItems?: ConfigurationItemMap;
    relatedListsItems?: ConfigurationItemMap;
  },
  saltoObjectTypes?: Pick<{ objectType: string; label: string }, 'objectType' | 'label'>[],
  scrollContainerRef?:
    | React.RefObject<HTMLElement>
    | React.MutableRefObject<HTMLElement | undefined>,
  activeStates?: SectionActiveStates,
): SearchResult[] => {
  const results: SearchResult[] = [];
  const lowerSearchTerm = searchTerm.toLowerCase();
  // Use object to pass by reference for globalMatchIndex
  const globalMatchIndex = { value: 0 };

  // 1. Search OpenAI Section using DOM-based search
  if (isSectionReadyForDOMSearch('[data-section="openAi"]', scrollContainerRef)) {
    const domResults = searchInDOM(
      {
        section: 'openAi',
        field: 'content',
        searchTerm,
        sectionSelector: '[data-section="openAi"]',
        globalMatchIndex,
      },
      scrollContainerRef,
    );
    results.push(...domResults);
  }

  // 2. Search Details Section using DOM-based search

  if (isSectionReadyForDOMSearch('[data-section="details"]', scrollContainerRef)) {
    const domResults = searchInDOM(
      {
        section: 'details',
        field: 'content',
        searchTerm,
        sectionSelector: '[data-section="details"]',
        globalMatchIndex,
      },
      scrollContainerRef,
    );
    results.push(...domResults);
  }

  // Search Related Lists (part of Details section)
  if (allData.relatedListsItems) {
    const relatedListsResults = searchThroughTableItems(
      allData.relatedListsItems,
      'details',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      false, // Related Lists don't have active state filtering
    );
    results.push(...relatedListsResults);
  }

  // 3. Search ConfigurationsSection tables
  if (allData.configurationItems) {
    const configResults = searchThroughTableItems(
      allData.configurationItems,
      'configurations',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      activeStates?.configurationsActiveState === ActiveState.activeOnly,
    );
    results.push(...configResults);
  }

  // Search SweepConfigurationItems
  if (allData.sweepConfigurationItems) {
    const sweepConfigResults = searchThroughTableItems(
      allData.sweepConfigurationItems,
      'configurations',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      activeStates?.configurationsActiveState === ActiveState.activeOnly,
    );
    results.push(...sweepConfigResults);
  }

  // 4. Search DependenciesSection tables
  if (allData.dependsOnItems) {
    const dependsOnResults = searchThroughTableItems(
      allData.dependsOnItems,
      'dependencies',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      activeStates?.dependsOnActiveState === ActiveState.activeOnly,
    );
    results.push(...dependsOnResults);
  }

  // Search UsedBySection tables
  if (allData.usedByItems) {
    const usedByResults = searchThroughTableItems(
      allData.usedByItems,
      'usedBy',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      activeStates?.usedByActiveState === ActiveState.activeOnly,
    );
    results.push(...usedByResults);
  }

  // Search UsedBy Sweep Elements (e.g., Sweep Deduplications in Used by section)
  if (allData.usedBySweepItems) {
    const usedBySweepResults = searchThroughTableItems(
      allData.usedBySweepItems,
      'usedBy',
      lowerSearchTerm,
      globalMatchIndex,
      saltoObjectTypes,
      activeStates?.usedByActiveState === ActiveState.activeOnly,
    );
    results.push(...usedBySweepResults);
  }

  // 5. Search Latest Deployed Section using DOM-based search
  if (isSectionReadyForDOMSearch('[data-section="latestDeployed"]', scrollContainerRef)) {
    const domResults = searchInDOM(
      {
        section: 'latestDeployed',
        field: 'content',
        searchTerm,
        sectionSelector: '[data-section="latestDeployed"]',
        globalMatchIndex,
      },
      scrollContainerRef,
    );
    results.push(...domResults);
  }

  // 6. Search Tags Section using DOM-based search
  if (isSectionReadyForDOMSearch('[data-section="tags"]', scrollContainerRef)) {
    const domResults = searchInDOM(
      {
        section: 'tags',
        field: 'tag',
        searchTerm,
        sectionSelector: '[data-section="tags"]',
        globalMatchIndex,
      },
      scrollContainerRef,
    );
    results.push(...domResults);
  }

  // 7. Search Source Code Section using DOM-based search
  if (isSectionReadyForDOMSearch('[data-section="sourceCode"]', scrollContainerRef)) {
    const domResults = searchInDOM(
      {
        section: 'sourceCode',
        field: 'content',
        searchTerm,
        sectionSelector: '[data-section="sourceCode"]',
        globalMatchIndex,
      },
      scrollContainerRef,
    );
    results.push(...domResults);
  }

  return results;
};
